import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Form, Modal } from 'antd';
import { useMemo } from 'react';
import { reparentOrgUnitSchema, type OfficerDto, type OrgUnitDto, type Paginated } from '@pams/shared';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { descendantIdsOf } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';
import { useZodForm } from '@/lib/zodForm';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  REPARENTING IS AN EXPLICIT, CONFIRMED ACTION. `draggable` STAYS OFF.
 *
 *  FR-5.1.1 asks for "drag-to-reparent", and P1a deliberately does not ship it. The
 *  reasons, recorded here and in the CHANGELOG rather than left as an omission:
 *
 *   1. Moving a node rewrites the `ancestors` array of every descendant, and `ancestors`
 *      is what every scoping decision in the system reads. So this changes WHO CAN SEE
 *      WHOM (FR-3.4, FR-3.6). A gesture that can silently re-scope four hundred officers
 *      should not be a 200ms mouse movement with no confirmation step.
 *   2. AntD's `onDrop` conflates "reorder among siblings" with "become a child of" via
 *      `dropToGap`. There is no sibling ordering in this model, so half of the gesture's
 *      outcomes are meaningless and the other half are destructive.
 *   3. Drag is not keyboard accessible without a parallel path, so the explicit action
 *      has to exist anyway. Building it twice to satisfy a phrasing is the wrong order.
 *
 *  Drag can be layered on later as a shortcut that opens THIS modal, which is the right
 *  end state either way.
 *
 *  ─── [amended P1a.2] THE BLAST RADIUS ─────────────────────────────────────────────
 *  The warning used to say a move re-scopes descendants. It now says HOW MANY, because
 *  "this changes who can see whom" is advice and "7 units and 112 officers move with
 *  this one" is a decision. Reparenting a Division silently is exactly the mistake that
 *  is hard to undo.
 *
 *  Two requests, both exact, both one round trip:
 *
 *    GET /org-units?rootId=<id>&includeInactive=true&limit=1  → total − 1   (rootId is
 *                                                                inclusive of the node)
 *    GET /officers?orgUnitId=<id>&includeDescendants=true&limit=1  → total
 *
 *  The client-side descendant count renders INSTANTLY as an optimistic value and is
 *  replaced when the server's lands. They agree in the common case; when they differ the
 *  server wins, and the difference is exactly the inactive or truncated units the client
 *  could not see.
 *
 *  Deliberately NO `isActive` filter on the officer count: an inactive officer's
 *  visibility is re-scoped by this move exactly as an active one's is. The copy says
 *  "officers posted", not "active officers".
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const MoveUnitModal = ({
  unit,
  allUnits,
  onClose,
}: {
  unit: OrgUnitDto;
  allUnits: readonly OrgUnitDto[];
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { form, field, submit, formErrors } = useZodForm(reparentOrgUnitSchema);

  /** The node itself and everything beneath it — the server rejects these too (409). */
  const forbidden = useMemo(() => [unit.id, ...descendantIdsOf(allUnits, unit.id)], [unit, allUnits]);

  /** Instant, and an UNDERCOUNT when the payload was truncated or filtered. */
  const optimisticDescendants = forbidden.length - 1;

  const [descendants, officers] = useQueries({
    queries: [
      {
        queryKey: queryKeys.orgUnits.list({
          rootId: unit.id,
          includeInactive: true,
          limit: 1,
          purpose: 'blast-radius',
        }),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          api.get<Paginated<OrgUnitDto>>('/org-units', {
            // `rootId` matches `{$or: [{_id}, {ancestors}]}` — INCLUSIVE of the node — so
            // the descendant count is one less than the total.
            query: { rootId: unit.id, includeInactive: true, page: 1, limit: 1 },
            signal,
          }),
        staleTime: 30_000,
      },
      {
        queryKey: queryKeys.officers.list({
          orgUnitId: unit.id,
          includeDescendants: true,
          limit: 1,
          purpose: 'blast-radius',
        }),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          api.get<Paginated<OfficerDto>>('/officers', {
            query: { orgUnitId: unit.id, includeDescendants: true, page: 1, limit: 1 },
            signal,
          }),
        staleTime: 30_000,
      },
    ],
  });

  const descendantCount = descendants?.data ? descendants.data.total - 1 : optimisticDescendants;
  const officerCount = officers?.data?.total;
  const radiusFailed = Boolean(descendants?.isError || officers?.isError);
  const radiusLoading = Boolean(descendants?.isLoading || officers?.isLoading);

  const blastRadius = (): string => {
    if (radiusFailed) {
      // ⚠️ A FAILED COUNT DOES NOT BLOCK THE MOVE. The counts are advisory context; the
      // server validates the reparent itself and is the authority on whether it is legal.
      // Blocking a WRITE on a failed READ would leave the only role that can perform this
      // action with no path forward but a reload — a degraded experience turned into an
      // outage.
      return (
        'Could not work out how much moves with this unit. ' +
        'Anyone scoped to the old parent will stop seeing it; anyone scoped to the new one will start.'
      );
    }

    const officersText =
      radiusLoading || officerCount === undefined ? 'the officers posted beneath it' : `${officerCount} officer(s)`;

    if (descendantCount > 0) {
      return (
        `${descendantCount} unit(s) and ${officersText} move with ${unit.name}. ` +
        'Anyone scoped to the old parent will stop seeing them; anyone scoped to the new one will start.'
      );
    }

    if (officerCount === 0) {
      return `Nothing is posted beneath ${unit.name}, so only the unit itself moves.`;
    }

    return (
      `${officersText} posted to ${unit.name} move with it. ` +
      'Anyone scoped to the old parent will stop seeing them; anyone scoped to the new one will start.'
    );
  };

  const move = useMutation({
    mutationFn: (values: { parentId: string | null }) => api.post<OrgUnitDto>(`/org-units/${unit.id}/reparent`, values),
    onSuccess: () => {
      void message.success(`${unit.name} moved`);
      // Everything under orgUnits AND officers: scope changed, so both lists are stale.
      void queryClient.invalidateQueries({ queryKey: queryKeys.orgUnits.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.officers.all });
      onClose();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  return (
    <Modal
      open
      title={`Move ${unit.name}`}
      okText="Move unit"
      confirmLoading={move.isPending}
      onCancel={onClose}
      onOk={() => form.submit()}
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message="This changes who can see whom"
        description={blastRadius()}
        data-testid="move-blast-radius"
      />

      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ parentId: unit.parentId }}
        onFinish={(values) => {
          const parsed = submit(values);
          if (parsed.ok) move.mutate(parsed.data);
        }}
      >
        <Form.Item {...field('parentId')} label="New parent unit">
          <OrgUnitPicker allowClear disabledIds={forbidden} placeholder="Select the new parent" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
